/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { TrackingConsent } from '@/types/tracking-consent';
import { resourceRoutes } from '@/route-paths';
import { ONETRUST_CONSENT_CHANGED_EVENT, mapActiveGroupsToTrackingConsent, readActiveGroups } from './mapping';

/**
 * OneTrust CMP → Storefront Next consent bridge (INT-60 PR 3).
 *
 * When `appConfig.onetrust.enabled === true`, this client component subscribes
 * to the same-origin `ashley:onetrust:consent-changed` `CustomEvent` fired by
 * the inline `OptanonWrapper()` in `src/root.tsx`. On every consent decision
 * it reads `window.OnetrustActiveGroups`, maps the OneTrust group IDs against
 * `appConfig.onetrust.categoryMap`, and — when `bridgeDwDnt` is true — dispatches
 * a `TrackingConsent.Accepted | Declined` to `/action/update-tracking-consent`
 * so the SLAS token + `dw_dnt` cookie stay aligned with the OT decision.
 *
 * Fire-and-forget: the fetcher submit is not awaited and never blocks banner
 * interaction. Idempotent: the last-sent decision is memoised, so a repeated
 * identical `OnetrustActiveGroups` payload (which OT can emit multiple times
 * — e.g. on initial-load geo classification followed by an implicit consent
 * event) does not accumulate duplicate SLAS refresh calls.
 *
 * Renders nothing. Delegated by `src/root.tsx` — do not mount this component
 * outside the root; its lifecycle must match the app shell so the `window`
 * subscription lives for the entire session.
 *
 * PR 4 will additionally rewrite `buildConsentPreferences()` to read
 * `window.OnetrustActiveGroups` directly so engagement adapters gate per
 * category. This component is the shim that keeps the binary `dw_dnt` path
 * (used by SFCC server-side Active Data + Einstein) in sync with the CMP for
 * as long as the two systems co-exist. See INT-60 §"OneTrust ⇄ dw_dnt bridging".
 */
export function OneTrustBridge() {
    const config = useConfig();
    const fetcher = useFetcher();
    const lastSentRef = useRef<TrackingConsent | null>(null);
    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;

    const onetrust = config.onetrust;
    const enabled = onetrust?.enabled === true;
    const bridgeDwDnt = onetrust?.bridgeDwDnt !== false;
    const analyticsCategory = onetrust?.categoryMap?.analytics ?? 'C0002';
    const advertisingCategory = onetrust?.categoryMap?.advertising ?? 'C0004';

    useEffect(() => {
        if (!enabled || !bridgeDwDnt || typeof window === 'undefined') {
            return;
        }

        const handleConsentChanged = () => {
            const consent = mapActiveGroupsToTrackingConsent(readActiveGroups(), {
                analytics: analyticsCategory,
                advertising: advertisingCategory,
            });
            if (consent === null || lastSentRef.current === consent) {
                return;
            }
            lastSentRef.current = consent;
            void fetcherRef.current.submit(
                { trackingConsent: consent },
                { method: 'POST', action: resourceRoutes.updateTrackingConsent }
            );
        };

        window.addEventListener(ONETRUST_CONSENT_CHANGED_EVENT, handleConsentChanged);
        // Handle the case where OneTrust has already dispatched its first
        // OptanonWrapper() before this component mounts (script may finish
        // loading during hydration, especially on fast connections). The
        // inline OptanonWrapper() sets a boolean flag we can check.
        if (typeof window.__ashleyOnetrustReady === 'boolean' && window.__ashleyOnetrustReady) {
            handleConsentChanged();
        }
        return () => {
            window.removeEventListener(ONETRUST_CONSENT_CHANGED_EVENT, handleConsentChanged);
        };
    }, [enabled, bridgeDwDnt, analyticsCategory, advertisingCategory]);

    return null;
}

/**
 * Ambient window augmentation for the OneTrust globals this bridge reads.
 * These are set by `otSDKStub.js` (external vendor script) — declared here
 * so consumers get typing without leaking to library authors that would need
 * to `declare global` in every file.
 */
declare global {
    interface Window {
        /** Comma-separated OneTrust active group IDs (e.g. `",C0001,C0002,"`). */
        OnetrustActiveGroups?: string;
        /** Set to `true` by the inline `OptanonWrapper()` after OT's first invocation. */
        __ashleyOnetrustReady?: boolean;
    }
}
